import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { get, flowRight as compose } from 'lodash';

import { GET_CART, GET_ACCOUNT_INFO } from 'mocks/queries';
import getZipcode from 'commons/getZipcode';
import useAffirmScript from 'hooks/useAffirmScript';
import parseCartItemErrors from 'commons/parseCartItemErrors';
import getStoreDetails from 'commons/getStoreDetails';
import { getItemFromStorage } from 'commons/localStorage';
import { FB_LOGIN_PII } from 'commons/constants';
import useUserData from 'data-hooks/useUserData';
import CheckoutLoader from './components/CheckoutLoader';
import withCheckoutUtils from '../withCheckoutUtils';
import { getCheckoutToken } from '../utils/checkout';

const tabsConfig = [
  {
    key: 'email',
    name: 'Email',
    progressTitle: 'Email',
  },
  {
    key: 'shipping',
    name: 'Shipping',
    progressTitle: 'Shipping',
  },
  {
    key: 'delivery',
    name: 'Delivery',
    progressTitle: 'Delivery',
  },
  {
    key: 'protection',
    name: 'Protection',
    progressTitle: 'Protection',
  },
  {
    key: 'payment',
    name: 'Payment',
    progressTitle: 'Billing',
  },
];

const parseCartForIssues = cart => {
  const issues = [];
  let cartIssueType = null;
  if (cart && cart.length) {
    cart.forEach(prod => {
      const { issue, issueType } = parseCartItemErrors(
        prod && prod.cart_item_errors
      );
      if (issue) {
        issues.push(prod);
        cartIssueType = issueType;
      }
    });
  }
  return { issues, issueType: cartIssueType };
};

const getTabs = cart =>
  cart?.some(cartItem => cartItem.eligible_protection_plans?.length)
    ? [...tabsConfig]
    : tabsConfig.filter(tab => tab.key !== 'protection');

const withAffirm = WrappedComponent => props => {
  // load affirm if not loaded
  useAffirmScript();

  return <WrappedComponent {...props} />;
};

const withUserDetailsForCheckout = ComponentToWrap => {
  const WithUserDetailsForCheckout = props => {
    const { email: userEmail, isTradeUser, userId } = useUserData();

    return (
      <ComponentToWrap
        {...props}
        isTradeUser={isTradeUser}
        userId={userId}
        userEmail={userEmail}
      />
    );
  };

  WithUserDetailsForCheckout.displayName = `withUserDetailsForCheckout(${ComponentToWrap.displayName ||
    ComponentToWrap.name})`;

  return WithUserDetailsForCheckout;
};

export const withData = WrappedComponent => {
  class CheckoutHOC extends React.Component {
    state = {
      loading: true,
      cartData: [],
      initialState: {},
      tabs: [...tabsConfig],
    };

    async componentDidMount() {
      const checkoutRef = this.props.checkoutUtilActions.getCheckoutRef();
      const [getAccountInfo, getCart, rehydrateData] = await Promise.all([
        this.props.client.query({
          query: GET_ACCOUNT_INFO,
          fetchPolicy: 'network-only',
        }),
        this.props.client.query({
          query: GET_CART,
          variables: {
            checkoutRef,
            zipCode: getZipcode(),
          },
          fetchPolicy: 'network-only',
        }),
        this.props.checkoutUtilActions.rehydrateCheckouts(),
      ]);

      const cartData = get(getCart, 'data.getCart.cart') || [];
      const accountInfoData = get(getAccountInfo, 'data.getAccountInfo') || {};

      const fbUser = getItemFromStorage(FB_LOGIN_PII);

      let initialState = {};

      // set from account info
      if (accountInfoData.success) {
        initialState.email_address = accountInfoData.email
          ? accountInfoData.email
          : fbUser.email || '';
        initialState.shipping = {
          first_name: accountInfoData.first_name,
          last_name: accountInfoData.last_name,
          address: accountInfoData.street2 || '',
          city: accountInfoData.city || '',
          apartment: accountInfoData.street1 || '',
          company: '',
          state: accountInfoData.state || '',
          zipcode: accountInfoData.zip || '',
          phone_number: accountInfoData.phone || '',
          shipping_sms: false,
          commercial_address: '0',
        };
      }

      // https://stitchindustries.atlassian.net/browse/CON-1846
      // fetch email from cache if network call fails.
      if (!initialState.email_address && this.props?.userEmail) {
        initialState.email_address = this.props?.userEmail;
      }

      // set "email and shipping" section from rehydrate state
      const { deliveryInfo, ...rehydrateState } = rehydrateData;
      initialState = { ...initialState, ...rehydrateState };

      // get shipping
      let shipping = {};
      if (
        initialState.shipping &&
        initialState.shipping.state &&
        initialState.shipping.zipcode
      ) {
        shipping = initialState.shipping;
      } else {
        // initialize with visitor zip
        shipping.zipcode = getZipcode();
      }

      // on affirm payment
      if (getCheckoutToken()) {
        initialState.affirmOrderInProgress = true;
        initialState.deliveryInfo = deliveryInfo || {
          delivery_selection: {},
          production_selection: {},
          production_selection_add: {},
        };

        // get shipping groups & order summary
        const [shippingGroupsInfo, orderSummary] = await Promise.all([
          this.props.checkoutUtilActions.getShippingGroups(
            shipping,
            deliveryInfo
          ),
          this.props.checkoutUtilActions.calculateTotal({
            email_address: initialState.email_address,
            shipping,
            deliveryInfo,
          }),
        ]);
        initialState.orderSummary = orderSummary;
        initialState.shippingGroups = shippingGroupsInfo.shippingGroups;
        initialState.errors = shippingGroupsInfo.errors || orderSummary.errors;
      } else {
        // shipping groups
        const shippingGroupsInfo = await this.props.checkoutUtilActions.getShippingGroups(
          shipping,
          deliveryInfo
        );
        initialState.errors = shippingGroupsInfo.errors;
        initialState.deliveryInfo = shippingGroupsInfo.deliveryInfo || {
          delivery_selection: {},
          production_selection: {},
          production_selection_add: {},
        };
        initialState.shippingGroups = shippingGroupsInfo.shippingGroups;

        // order summary
        initialState.orderSummary = {};
        if (!shippingGroupsInfo.errors) {
          const orderSummary = await this.props.checkoutUtilActions.calculateTotal(
            {
              email_address: initialState.email_address,
              shipping,
              deliveryInfo: initialState.deliveryInfo,
            }
          );
          initialState.errors = orderSummary?.errors ?? null;
          initialState.orderSummary = orderSummary ?? {};
        }
      }

      const tabs = getTabs(cartData);
      const { issues, issueType } = parseCartForIssues(cartData);

      initialState.posData = {};
      const storeDetails = getStoreDetails();
      if (storeDetails?.id) {
        initialState.posData = {
          storeId: storeDetails?.id,
        };
      }

      this.setState({
        tabs,
        cartData,
        initialState,
        loading: false,
        cartIssues: issues,
        cartIssueType: issueType,
        checkoutSummaryData: { checkoutSummary: initialState.orderSummary },
      });
    }

    getCartIssues = async shippingZip => {
      const cartData = await this.props.checkoutUtilActions.getCart(
        shippingZip
      );
      const cart = cartData ? cartData.cart : [];
      const tabs = getTabs(cart);
      const { issues, issueType } = parseCartForIssues(cart);
      this.setState({
        tabs,
        cartData: cart,
        cartIssues: issues,
        cartIssueType: issueType,
      });
      return { cart, issues };
    };

    render() {
      // render loading
      if (this.state.loading) {
        return <CheckoutLoader />;
      }

      return (
        <WrappedComponent
          {...this.props}
          tabs={this.state.tabs}
          getCartIssues={this.getCartIssues}
          cartData={this.state.cartData}
          cartIssues={this.state.cartIssues}
          cartIssueType={this.state.cartIssueType}
          withDataState={this.state.initialState}
          checkoutSummaryData={this.state.checkoutSummaryData}
        />
      );
    }
  }

  CheckoutHOC.propTypes = {
    checkoutUtilActions: PropTypes.object,
    client: PropTypes.object,
    userEmail: PropTypes.string,
  };

  return compose(
    withCheckoutUtils,
    withRouter,
    withAffirm,
    withUserDetailsForCheckout
  )(CheckoutHOC);
};
